import { Box } from '@mui/material'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Sidebar } from './components/Sidebar'
import { Header, DASHBOARD_MODES } from './components/Header/Header'
import { Dashboard } from './pages/Dashboard'
import { Settings } from './pages/Settings'
import PropensityPredictions from './pages/PropensityPredictions'
import { ExcelUpload } from './pages/ExcelUpload'
import SmsTrigger from './pages/SmsTrigger'
import SmsScheduler from './pages/SmsScheduler'
import SmsTemplateManagement from './pages/SmsTemplateManagement'
import RenewalDashboard from './pages/RenewalDashboard'
import { useState, useEffect, useRef, useCallback, createContext, useContext, useMemo, memo } from 'react'
import Keycloak from 'keycloak-js'
import axiosInstance from './utils/axios'
import { Provider } from 'react-redux'
import { store } from './store/store'
import { DashboardViewProvider } from './contexts/DashboardViewContext'
import { AuthProvider } from './contexts/AuthContext'
import { FilterVisibilityProvider, useFilterVisibility } from './contexts/FilterVisibilityContext'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import { logLastApiCall } from './utils/axios'
import { LoadingProvider } from './contexts/LoadingContext'
import { LoadingOverlay } from './components/ui/LoadingOverlay'
import { useIdleTimer } from 'react-idle-timer'
import { useBrowserZoom } from './hooks/useBrowserZoom'

// Create enhanced MUI theme with design tokens from Tailwind CSS variables
const theme = createTheme({
  palette: {
    primary: {
      main: '#003087',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#E31837',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
    error: {
      main: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
    },
    info: {
      main: '#3b82f6',
    },
    success: {
      main: '#16a34a',
    },
    divider: '#e5e7eb',
  },
  typography: {
    fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          padding: '24px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
})

// Define types for Keycloak instance and token
interface KeycloakTokenParsed {
  exp?: number;
  iat?: number;
  jti?: string;
  iss?: string;
  aud?: string;
  sub?: string;
  typ?: string;
  azp?: string;
  nonce?: string;
  session_state?: string;
  acr?: string;
  allowed_origins?: string[];
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles?: string[];
    };
  };
  email?: string;
  email_verified?: boolean;
  preferred_username?: string;
}

interface KeycloakInstance extends Keycloak {
  token?: string;
  tokenParsed?: KeycloakTokenParsed;
  init: (options: any) => Promise<boolean>;
  updateToken: (minValidity: number) => Promise<boolean>;
  logout: (options?: { redirectUri?: string }) => void;
  idToken?: string;
  onTokenExpired?: () => void;
}

// Define the Keycloak constructor type
interface KeycloakConstructor {
  new (config: any): KeycloakInstance;
}

const initOptions = {
  url: 'https://neokartuatacc.cholainsurance.com',
  realm: 'neural',
  clientId: 'web',
  credentials: { secret: 'Eyv74aPSg3Lbw6OuTeGfLJlquEaFtwic' },
  onLoad: "login-required",
};


// Create a context for authentication with proper types
interface AuthContextType {
  logout: () => void;
  isAuthenticated: boolean;
  token: string | null;
  userEmail: string | null;
  username: string | null;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  logout: () => {},
  isAuthenticated: false,
  token: null,
  userEmail: null,
  username: null,
  isAdmin: false
});

// Create a context for dashboard view mode
interface DashboardViewContextType {
  viewMode: string;
  setViewMode: (mode: string) => void;
}

export const DashboardViewContext = createContext<DashboardViewContextType>({
  viewMode: DASHBOARD_MODES.STANDARD,
  setViewMode: () => {}
});

// Hook for using the dashboard view context
export function useDashboardView() {
  return useContext(DashboardViewContext);
}

// Debounce function to prevent rapid state updates
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function executedFunction(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Memoize the MainContent component to prevent unnecessary re-renders
const MainContent = memo(function MainContent() {
  const { mode, toggleColorMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState(DASHBOARD_MODES.STANDARD);
  const { showFilters, toggleFilters } = useFilterVisibility();
  const { systemScaling, zoom, scale } = useBrowserZoom();
  
  const toggleDrawer = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const dashboardViewValue = useMemo(() => ({
    viewMode,
    setViewMode
  }), [viewMode]);

  return (
    <DashboardViewContext.Provider value={dashboardViewValue}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        overflow: 'hidden'
      }}>
        <Header 
          isOpen={isOpen}
          toggleDrawer={toggleDrawer}
          mode={mode}
          toggleColorMode={toggleColorMode}
          viewMode={viewMode}
          setViewMode={setViewMode}
          primaryColor="#003087"
          secondaryColor="#E31837"
          showFilters={showFilters}
          onToggleFilters={toggleFilters}
        />
        
        <Box sx={{ 
          display: 'flex', 
          flex: 1,
          overflow: 'hidden'
        }}>
          <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              backgroundColor: 'background.default',
              transition: 'margin 0.2s ease',
              p: 3,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 'calc(100vh - 64px)',
              maxHeight: 'calc(100vh - 64px)',
              // Apply CSS zoom compensation for system scaling (e.g., 150% resolution)
              // CSS zoom scales everything proportionally including fonts, spacing, and layout
              // For 150% system scaling, zoom: 0.6667 makes it appear as 100%
              zoom: systemScaling > 100 ? scale : 1,
              '& #executive-dashboard': {
                paddingTop: '2px !important',
                '& > *': {
                  paddingTop: '2px !important',
                }
              },
              '& .css-1sf3xto': {
                paddingTop: '2px !important',
              }
            }}
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/propensity" element={<PropensityPredictions />} />
              <Route path="/excel-upload" element={<AdminRoute element={<ExcelUpload />} />} />
              <Route path="/sms-trigger" element={<AdminRoute element={<SmsTrigger />} />} />
              <Route path="/sms-scheduler" element={<AdminRoute element={<SmsScheduler />} />} />
              <Route path="/sms-templates" element={<AdminRoute element={<SmsTemplateManagement />} />} />
              <Route path="/renewal-dashboard" element={<AdminRoute element={<RenewalDashboard />} />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    </DashboardViewContext.Provider>
  );
});

// AdminRoute component to restrict access to admin-only routes
interface AdminRouteProps {
  element: React.ReactElement;
}

const AdminRoute = ({ element }: AdminRouteProps) => {
  const { isAdmin, isAuthenticated, token } = useContext(AuthContext);
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  useEffect(() => {
    // Give more time for token parsing, especially on slower connections
    // Wait up to 2 seconds for authentication and admin status to be determined
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    if (!isAuthenticated || !token) {
      setIsChecking(true);
      // Wait for authentication
      return;
    }
    
    // Add a small delay to ensure token parsing (with debounce) has completed
    // This is especially important on slower connections or servers
    checkTimeoutRef.current = setTimeout(() => {
      setIsChecking(false);
    }, 500); // Increased from immediate check to 500ms to allow debounced token parsing
    
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [isAuthenticated, token, isAdmin]);
  
  // Show loading state while checking authentication and admin status
  if (!isAuthenticated || !token || isChecking) {
    return null; // Or return a loading spinner if preferred
  }
  
  // Only redirect if we're certain the user is not an admin
  // This prevents premature redirects due to timing issues
  if (!isAdmin) {
    console.log('AdminRoute: User is not an admin, redirecting to dashboard');
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }
  
  return element;
};

function App() {
  const [keycloak, setKeycloak] = useState<KeycloakInstance | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const interceptorIdRef = useRef<number | null>(null);
  const keycloakInstanceRef = useRef<KeycloakInstance | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const initializingRef = useRef(false);
  const tokenRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isComponentMountedRef = useRef(false);

  // Debounced token setter to prevent rapid state updates
  const debouncedSetToken = useCallback(
    debounce((newToken: string) => {
      setToken(newToken);
      
      // Parse the token to extract user information
      try {
        const base64Url = newToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const parsedToken = JSON.parse(jsonPayload) as KeycloakTokenParsed;
        
        // Extract email and username
        if (parsedToken.email) {
          setUserEmail(parsedToken.email);
        }
        
        if (parsedToken.preferred_username) {
          setUsername(parsedToken.preferred_username);
        }
        
        // Check if user has Admin role
        if (parsedToken.realm_access && parsedToken.realm_access.roles) {
          const hasAdminRole = parsedToken.realm_access.roles.includes('Admin');
          setIsAdmin(hasAdminRole);
        }
        
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }, 300),
    []
  );

  // Memoize the logout function to prevent unnecessary re-renders
  const logout = useCallback(() => {
    if (keycloakInstanceRef.current) {
      localStorage.removeItem("kc_token");
      
      // Construct the logout URL manually
      const keycloak = keycloakInstanceRef.current;
      const logoutUrl = `${initOptions.url}/realms/${initOptions.realm}/protocol/openid-connect/logout`;
      const postLogoutRedirectUri = encodeURIComponent(window.location.origin);
      const idTokenHint = keycloak.idToken;
      
      // Redirect to the logout URL with the correct parameters
      window.location.href = `${logoutUrl}?post_logout_redirect_uri=${postLogoutRedirectUri}&id_token_hint=${idTokenHint}`;
    }
  }, []);

  // Setup idle timer for automatic logout
  const onIdle = useCallback(() => {
    console.log('User has been idle for 15 mins, logging out');
    logout();
  }, [logout]);

  // Initialize idle timer with 15 mins timeout
  const { getRemainingTime } = useIdleTimer({
    timeout: 900000, // 15 mins
    onIdle,
    debounce: 500
  });

  // Memoize the auth context value to prevent unnecessary re-renders
  const authContextValue = useMemo(() => ({
    logout,
    isAuthenticated: authenticated,
    token,
    userEmail,
    username,
    isAdmin
  }), [logout, authenticated, token, userEmail, username, isAdmin]);

  // Setup token refresh mechanism
  const setupTokenRefresh = useCallback(() => {
    if (!keycloakInstanceRef.current) return;
    
    // Clear any existing refresh timeout
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
    }
    
    // Calculate time until token expiry (default to 50 seconds before expiry)
    const expiresIn = (keycloakInstanceRef.current.tokenParsed?.exp || 0) * 1000 - Date.now() - 50000;
    
    // Set a timeout to refresh the token
    if (expiresIn > 0) {
      tokenRefreshTimeoutRef.current = setTimeout(() => {
        if (keycloakInstanceRef.current) {
          keycloakInstanceRef.current.updateToken(30)
            .then((refreshed: boolean) => {
              if (refreshed && keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
                debouncedSetToken(keycloakInstanceRef.current.token);
                localStorage.setItem("kc_token", keycloakInstanceRef.current.token);
                // Setup the next refresh
                setupTokenRefresh();
              }
            })
            .catch(() => {
              console.error('Failed to refresh token');
              logout();
            });
        }
      }, expiresIn);
    } else {
      // Token is already expired or close to expiry, refresh immediately
      if (keycloakInstanceRef.current) {
        keycloakInstanceRef.current.updateToken(30)
          .then((refreshed: boolean) => {
            if (refreshed && keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
              debouncedSetToken(keycloakInstanceRef.current.token);
              localStorage.setItem("kc_token", keycloakInstanceRef.current.token);
              // Setup the next refresh
              setupTokenRefresh();
            }
          })
          .catch(() => {
            console.error('Failed to refresh token');
            logout();
          });
      }
    }
  }, [debouncedSetToken, logout]);

  // Add a small delay to ensure token is available before API calls
  const [isTokenReady, setIsTokenReady] = useState(false);

  useEffect(() => {
    if (!isComponentMountedRef.current) {
      isComponentMountedRef.current = true;
      
      // Skip initialization if already authenticated or if initialization is in progress
      if ((authenticated && keycloakInstanceRef.current) || initializingRef.current) {
        return;
      }

      // Set initializing flag to prevent multiple initialization attempts
      initializingRef.current = true;

      // Initialize Keycloak only once. Commented for testing purpose. Do not delete
      const initializeKeycloak = async () => {
        try {
          // Check if we already have an instance
          if (!keycloakInstanceRef.current) {
            // Create a new Keycloak instance with proper typing
            const keycloakInstance = new (Keycloak as unknown as KeycloakConstructor)(initOptions);
            keycloakInstanceRef.current = keycloakInstance;
          }
          
          // Initialize Keycloak
          if (keycloakInstanceRef.current) {
            const auth = await keycloakInstanceRef.current.init({
              onLoad: "login-required",
              checkLoginIframe: false,
              promiseType: "native",
              pkceMethod: "S256",
              enableLogging: true,
              token: localStorage.getItem("kc_token") || undefined
            });
            
            if (auth && keycloakInstanceRef.current.token) {
              keycloakInstanceRef.current.onTokenExpired = () => {
                console.log('Token expired, logging out automatically');
                logout();
              };
              setKeycloak(keycloakInstanceRef.current);
              setAuthenticated(true);
              debouncedSetToken(keycloakInstanceRef.current.token);
              localStorage.setItem("kc_token", keycloakInstanceRef.current.token);
              
              // Setup token refresh
              setupTokenRefresh();
              
              // Add a small delay to ensure token is available before API calls
              setTimeout(() => {
                setIsTokenReady(true);
              }, 2000); // 2 second delay
              
              // Remove existing interceptor if it exists
              if (interceptorIdRef.current !== null) {
                axiosInstance.interceptors.request.eject(interceptorIdRef.current);
              }
              
              // Set up axios auth header
              const interceptorId = axiosInstance.interceptors.request.use(
                (config) => {
                  if (keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
                    config.headers.Authorization = `Bearer ${keycloakInstanceRef.current.token}`;
                    
                    // Add user email to headers if available
                    if (userEmail) {
                      config.headers['X-User-Email'] = userEmail;
                    }
                  }
                  return config;
                },
                (error) => Promise.reject(error)
              );
              
              // Store the interceptor ID for cleanup
              interceptorIdRef.current = interceptorId;
            }
          }
        } catch (error) {
          console.error("Keycloak initialization failed:", error);
        } finally {
          // Reset initializing flag
          initializingRef.current = false;
        }
      };
      
      initializeKeycloak();
    }
    
    // Cleanup function
    return () => {
      // Remove interceptor when component unmounts
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
      
      // Clear token refresh timeout
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
      }
    };
  }, []); // Remove dependencies to only run on mount

  // Update userEmail in axios headers when it changes
  useEffect(() => {
    if (keycloakInstanceRef.current && userEmail) {
      // Re-create the interceptor with the updated userEmail
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
      }
      
      const interceptorId = axiosInstance.interceptors.request.use(
        (config) => {
          if (keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
            config.headers.Authorization = `Bearer ${keycloakInstanceRef.current.token}`;
            
            // Add user email to headers if available
            if (userEmail) {
              config.headers['X-User-Email'] = userEmail;
            }
          }
          return config;
        },
        (error) => Promise.reject(error)
      );
      
      interceptorIdRef.current = interceptorId;
    }
  }, [userEmail]);

  // Log authentication state only when it changes
  useEffect(() => {
    if (authenticated) {
      console.log('Authenticated:', authenticated);
    }
  }, [authenticated]);
  
  // Using useMemo for MainContent to prevent unnecessary re-renders
  const mainContentComponent = useMemo(() => {
    
    // Temporarily bypass authentication for debugging
    return <MainContent />;
    // if (authenticated && isTokenReady) {
    //   return <MainContent />;
    // }
    // return null;
  }, [authenticated, isTokenReady]);
  
  // In the App function, add a keyboard listener for debugging
  useEffect(() => {
    // Set up keyboard shortcut (Ctrl+Alt+L) to log the last API call
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+Alt+L
      if (event.ctrlKey && event.altKey && event.key === 'l') {
        logLastApiCall();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  return (
    <BrowserRouter>
      <Provider store={store}>
        <DashboardViewProvider>
          <FilterVisibilityProvider>
            <AuthContext.Provider value={authContextValue}>
              <LoadingProvider>
                <MuiThemeProvider theme={theme}>
                  {mainContentComponent}
                  <LoadingOverlay />
                </MuiThemeProvider>
              </LoadingProvider>
            </AuthContext.Provider>
          </FilterVisibilityProvider>
        </DashboardViewProvider>
      </Provider>
    </BrowserRouter>
  );
}

export default App 
